import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faEnvelope, faBell } from '@/fontawesome';
import Modal from '@/components/ui/Modal';
import CardCheckbox from '@/components/ui/CardCheckbox';
import {faDiscord, faTelegram} from "@fortawesome/free-brands-svg-icons";

interface PublishModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublish: (sendDiscord: boolean, sendTelegram: boolean, sendMail: boolean, sendPush: boolean) => Promise<void>;
  title: string;
  itemName: string;
  type: 'video' | 'course' | 'module';
}

const PublishModal: React.FC<PublishModalProps> = ({
  isOpen,
  onClose,
  onPublish,
  title,
  itemName,
  type
}) => {
  const [sendDiscord, setSendDiscord] = useState(true);
  const [sendTelegram, setSendTelegram] = useState(true);
  const [sendMail, setSendMail] = useState(true);
  const [sendPush, setSendPush] = useState(true);
  const [loading, setLoading] = useState(false);

  const handlePublish = async () => {
    setLoading(true);
    try {
      await onPublish(sendDiscord, sendTelegram, sendMail, sendPush);
      onClose();
    } catch {
      // Error handling is done in parent component
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = () => {
    switch (type) {
      case 'video':
        return 'Video';
      case 'course':
        return 'Kurs';
      case 'module':
        return 'Modul';
      default:
        return 'Element';
    }
  };

  const showNotificationOptions = type === 'video'; // Only videos support notification options

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      icon={<FontAwesomeIcon icon={faRocket} className="w-5 h-5 text-green-600" />}
      maxWidth="lg"
      closeDisabled={loading}
    >
      <div className="p-6">
        <p className="text-gray-700 mb-6">
          Möchten Sie das {getTypeLabel()} <span className="font-semibold">"{itemName}"</span> veröffentlichen?
        </p>

        {showNotificationOptions && (
          <div className="mb-6 space-y-3">
            <CardCheckbox
              checked={sendDiscord}
              onChange={setSendDiscord}
              disabled={loading}
              icon={<FontAwesomeIcon icon={faDiscord} className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />}
              label="Discord-Nachricht senden"
              description="Benachrichtigt alle Mitglieder in Discord über das neue Video"
            />

            <CardCheckbox
              checked={sendTelegram}
              onChange={setSendTelegram}
              disabled={loading}
              icon={<FontAwesomeIcon icon={faTelegram} className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />}
              label="Telegram-Nachricht senden"
              description="Sendet eine Nachricht in den Telegram Channel"
            />

            <CardCheckbox
              checked={sendMail}
              onChange={setSendMail}
              disabled={loading}
              icon={<FontAwesomeIcon icon={faEnvelope} className="w-4 h-4 text-blue-600 dark:text-blue-400 mr-2" />}
              label="E-Mail-Benachrichtigung senden"
              description="Sendet eine E-Mail an alle Abonnenten mit aktivierten Benachrichtigungen"
            />

            <CardCheckbox
              checked={sendPush}
              onChange={setSendPush}
              disabled={loading}
              icon={<FontAwesomeIcon icon={faBell} className="w-4 h-4 text-purple-600 dark:text-purple-400 mr-2" />}
              label="Push-Benachrichtigung senden"
              description="Sendet eine Push-Benachrichtigung an alle Nutzer mit aktivierter App-Benachrichtigung"
            />

          </div>
        )}

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            onClick={handlePublish}
            disabled={loading}
            className="px-4 py-2 text-white bg-green-600 rounded-lg hover:bg-green-700 font-medium transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Veröffentlichen...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faRocket} className="w-4 h-4" />
                <span>Veröffentlichen</span>
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default PublishModal;